import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { getSubscriptionStatus } from "@/lib/api";
import { AlertTriangle, ArrowRight, ScanBarcode, CalendarClock } from "lucide-react";

/**
 * Subscription banner (paid-only model). Hidden when active and > 5 days left.
 * pending_payment → "Activa tu plan" · expired/suspended → lock notice · active & ≤5 días → renewal reminder.
 */
export default function TrialBanner() {
  const [sub, setSub] = useState(null);

  useEffect(() => {
    let mounted = true;
    getSubscriptionStatus().then((s) => { if (mounted) setSub(s); }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  if (!sub) return null;
  const planName = sub.plan_info?.name || (sub.plan === "onepay" ? "ONE PAY" : "CHALANITO");
  const price = `$${sub.plan_price_usd} USD/${sub.plan === "onepay" ? "año" : "mes"}`;

  if (sub.status === "active") {
    if (sub.days_left > 5) return null;
    return (
      <motion.div initial={{ y: -10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="mb-4 rounded-2xl border border-[#ccff00]/40 bg-[#ccff00]/5 p-3 md:p-4 flex items-center gap-3" data-testid="sub-banner-renew">
        <span className="w-9 h-9 rounded-xl bg-[#ccff00] text-black flex items-center justify-center flex-shrink-0"><CalendarClock size={16} strokeWidth={2.5} /></span>
        <div className="flex-1 min-w-0">
          <p className="font-display text-base md:text-lg tracking-tight leading-tight">Tu plan {planName} vence en <span className="text-[#ccff00]">{sub.days_left}</span> día{sub.days_left !== 1 ? "s" : ""}</p>
          <p className="text-white/50 text-xs md:text-sm mt-0.5">Renueva por {price} para no interrumpir tus ventas.</p>
        </div>
        <Link to="/pagar" className="inline-flex items-center gap-1.5 border border-white/15 text-white hover:bg-white hover:text-black font-display text-sm tracking-tight px-4 py-2 rounded-full whitespace-nowrap" data-testid="sub-banner-renew-pay">Renovar <ArrowRight size={14} /></Link>
      </motion.div>
    );
  }

  const expired = sub.status === "expired" || sub.status === "suspended";
  return (
    <motion.div initial={{ y: -10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.4 }}
      className="mb-4 rounded-2xl border border-[#ff5a00]/50 bg-[#ff5a00]/10 p-4 flex items-start gap-3"
      data-testid={expired ? "trial-banner-expired" : "sub-banner-pending"}>
      {sub.plan === "onepay" ? <ScanBarcode size={20} className="text-[#ff5a00] flex-shrink-0 mt-0.5" /> : <AlertTriangle size={20} className="text-[#ff5a00] flex-shrink-0 mt-0.5" />}
      <div className="flex-1">
        <p className="font-display text-lg tracking-tight leading-tight">
          {sub.status === "suspended" ? "Tu tienda está suspendida" : expired ? "Tu periodo terminó" : `Activa tu plan ${planName}`}
        </p>
        <p className="text-white/70 text-sm mt-1">
          {expired ? "Renueva" : "Paga"} <span className="text-[#ccff00] font-bold">{price}</span> con tarjeta o transferencia SPEI para {expired ? "seguir" : "empezar a"} cobrar. Mientras tanto puedes cargar tu inventario.
        </p>
      </div>
      <Link to="/pagar" className="inline-flex items-center gap-1.5 bg-[#ff5a00] hover:bg-[#ff7a30] text-black font-display text-sm md:text-base tracking-tight px-4 py-2 rounded-full whitespace-nowrap" data-testid="trial-banner-pay">
        Pagar ahora <ArrowRight size={14} />
      </Link>
    </motion.div>
  );
}
